import {
  AuthScopes,
  ClaimReservationWithTimeslot,
  FeatureFlag,
} from '@Generated/back-office-client/src'
import { Checkbox, Em, Flex, Select, Text, TextArea, TextField } from '@radix-ui/themes'
import { formatDateComponentsToDayString } from '../util/utils'
import { FileTextIcon } from '@radix-ui/react-icons'
import { WithPermissions } from '@Components/common/with-permissions'
import { formatPhoneNumber } from './phone-utils'
import { useSelector } from 'react-redux'
import { RootState } from '@AppState/redux-store'
import { DEFAULT_MAX_TICKETS_PER_RESERVATION } from './types'

type ReservationInfoProps = {
  reservation: ClaimReservationWithTimeslot
  onClaimFormClick?: (reservationId: string) => void
  isCheckedIn: boolean
  isNoShow: boolean
  onCheckedInChange: (checked: boolean) => void
  onNoShowChange: (checked: boolean) => void
  isEditing: boolean
  claimFormsEnabled: boolean
  selectedDate: string
  onDateChange: (date: string) => void
  selectedTimeslotId: string
  onTimeslotChange: (timeslotId: string) => void
  availableTimeslots: { id: string; display: { localTime: string } }[]
  isAvailabilityLoading: boolean
  phone: string
  onPhoneChange: (phone: string) => void
  email: string
  onEmailChange: (email: string) => void
  notes: string
  onNotesChange: (notes: string) => void
  numberOfTickets: number
  onNumberOfTicketsChange: (n: number) => void
  claimFormReceived?: boolean
  hasLinkedClaimForm?: boolean
  hasTicketCountChanged?: boolean
}

export const ReservationInfo = ({
  reservation,
  onClaimFormClick,
  isCheckedIn,
  isNoShow,
  onCheckedInChange,
  onNoShowChange,
  isEditing,
  claimFormsEnabled,
  selectedDate,
  onDateChange,
  selectedTimeslotId,
  onTimeslotChange,
  availableTimeslots,
  isAvailabilityLoading,
  phone,
  onPhoneChange,
  email,
  onEmailChange,
  notes,
  onNotesChange,
  numberOfTickets,
  onNumberOfTicketsChange,
  claimFormReceived,
  hasLinkedClaimForm,
  hasTicketCountChanged,
}: ReservationInfoProps) => {
  const maxTicketsPerReservation =
    useSelector(
      (state: RootState) => state.users.activeCustomerMetadata?.maxTicketsPerReservation,
    ) ?? DEFAULT_MAX_TICKETS_PER_RESERVATION
  return (
    <>
      <Flex gap="3" align="end">
        <Text weight="bold">{reservation.claimReservationDetails.name}</Text>
        <Text size="1">
          <Em>
            {reservation.claimReservationDetails.editedByAgentName
              ? `edited by ${reservation.claimReservationDetails.editedByAgentName}`
              : `created by ${reservation.claimReservationDetails.createdByAgentName ?? 'player'}`}
          </Em>
        </Text>
      </Flex>

      <Flex gap="2" align="center">
        <Text size="2">
          {formatDateComponentsToDayString(reservation.timeslot.display.dateComponents)}
        </Text>
        <Text size="2">&#8729;</Text>
        <Text size="2">{reservation.timeslot.display.localTime}</Text>
        <Text size="2">&#8729;</Text>
        <Text size="2">
          <Em>Est. time: {reservation.timeslot.display.duration}</Em>
        </Text>
        {reservation.claimFormId && onClaimFormClick ? (
          <button
            className={`claim-form-icon ${reservation.claimFormAuthorized ? 'authorized' : ''}`}
            style={{ position: 'static' }}
            onClick={(e) => {
              e.stopPropagation()
              onClaimFormClick(reservation.id)
            }}
            aria-label="View claim form">
            <FileTextIcon />
          </button>
        ) : null}
      </Flex>

      <WithPermissions
        requiredPermissions={AuthScopes.writeclaim_center_reservations}
        requiredFeature={FeatureFlag.claim_forms}>
        <Flex gap="4" align="center">
          <Flex align="center" gap="2" asChild>
            <label>
              <Checkbox checked={isCheckedIn} onClick={() => onCheckedInChange(!isCheckedIn)} />
              <Text size="2">Checked In</Text>
            </label>
          </Flex>
          <Flex align="center" gap="2" asChild>
            <label>
              <Checkbox checked={isNoShow} onClick={() => onNoShowChange(!isNoShow)} />
              <Text size="2">No Show</Text>
            </label>
          </Flex>
        </Flex>
      </WithPermissions>

      {isEditing ? (
        <EditableFields
          claimFormsEnabled={claimFormsEnabled}
          selectedDate={selectedDate}
          onDateChange={onDateChange}
          selectedTimeslotId={selectedTimeslotId}
          onTimeslotChange={onTimeslotChange}
          availableTimeslots={availableTimeslots}
          isAvailabilityLoading={isAvailabilityLoading}
          phone={phone}
          onPhoneChange={onPhoneChange}
          email={email}
          onEmailChange={onEmailChange}
          notes={notes}
          onNotesChange={onNotesChange}
          numberOfTickets={numberOfTickets}
          onNumberOfTicketsChange={onNumberOfTicketsChange}
          claimFormReceived={claimFormReceived}
          hasLinkedClaimForm={hasLinkedClaimForm}
          hasTicketCountChanged={hasTicketCountChanged}
          maxTicketsPerReservation={maxTicketsPerReservation}
        />
      ) : (
        <>
          <Flex gap="1">
            <Text weight="bold">Number of Tickets:</Text>
            <Text>{reservation.claimReservationDetails.numberOfTickets}</Text>
          </Flex>
          <ReadOnlyFields reservation={reservation} />
        </>
      )}
    </>
  )
}

const ReadOnlyFields = ({ reservation }: { reservation: ClaimReservationWithTimeslot }) => {
  return (
    <Flex gap="1" direction="column">
      <Flex gap="1">
        <Text weight="bold">Phone Number:</Text>
        <Text>{reservation.claimReservationDetails.phone}</Text>
      </Flex>
      <Flex gap="1">
        <Text weight="bold">Email:</Text>
        <Text>{reservation.claimReservationDetails.email}</Text>
      </Flex>
      <Flex gap="1">
        <Text weight="bold">Notes:</Text>
        <Text>{reservation.claimReservationDetails.notes ?? 'N/A'}</Text>
      </Flex>
    </Flex>
  )
}

type EditableFieldsProps = Pick<
  ReservationInfoProps,
  | 'claimFormsEnabled'
  | 'selectedDate'
  | 'onDateChange'
  | 'selectedTimeslotId'
  | 'onTimeslotChange'
  | 'availableTimeslots'
  | 'isAvailabilityLoading'
  | 'phone'
  | 'onPhoneChange'
  | 'email'
  | 'onEmailChange'
  | 'notes'
  | 'onNotesChange'
  | 'numberOfTickets'
  | 'onNumberOfTicketsChange'
  | 'claimFormReceived'
  | 'hasLinkedClaimForm'
  | 'hasTicketCountChanged'
> & { maxTicketsPerReservation: number }

const EditableFields = ({
  claimFormsEnabled,
  selectedDate,
  onDateChange,
  selectedTimeslotId,
  onTimeslotChange,
  availableTimeslots,
  isAvailabilityLoading,
  phone,
  onPhoneChange,
  email,
  onEmailChange,
  notes,
  onNotesChange,
  numberOfTickets,
  onNumberOfTicketsChange,
  claimFormReceived,
  hasLinkedClaimForm,
  hasTicketCountChanged,
  maxTicketsPerReservation,
}: EditableFieldsProps) => {
  if (!claimFormsEnabled) {
    return (
      <Flex direction="column">
        <Text weight="bold">Notes</Text>
        <TextArea
          placeholder="Add Notes"
          className="add-notes"
          maxLength={255}
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
        />
      </Flex>
    )
  }

  return (
    <>
      <Flex gap="3">
        <Flex direction="column" style={{ flex: 1 }}>
          <Text weight="bold">Appointment Date</Text>
          <input
            type="date"
            className="rt-reset rt-SelectTrigger rt-r-size-2 rt-variant-surface"
            value={selectedDate}
            onChange={(e) => onDateChange(e.target.value)}
          />
        </Flex>
        <Flex direction="column" style={{ flex: 1 }}>
          <Text weight="bold">Start Time</Text>
          {isAvailabilityLoading ? (
            <Text size="2">Loading...</Text>
          ) : availableTimeslots.length > 0 ? (
            <Select.Root value={selectedTimeslotId} onValueChange={onTimeslotChange}>
              <Select.Trigger />
              <Select.Content>
                {availableTimeslots.map((t) => (
                  <Select.Item key={t.id} value={t.id}>
                    {t.display.localTime}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          ) : (
            <Text size="2" color="red">
              No availability for selected date
            </Text>
          )}
        </Flex>
        <Flex direction="column" style={{ flex: 1 }}>
          <Text weight="bold">Number of Tickets</Text>
          <Select.Root
            value={numberOfTickets.toString()}
            onValueChange={(v) => onNumberOfTicketsChange(Number.parseInt(v, 10))}
            disabled={claimFormReceived}>
            <Select.Trigger />
            <Select.Content>
              {Array.from({ length: maxTicketsPerReservation - 1 }, (_, i) => (
                <Select.Item key={i + 1} value={(i + 1).toString()}>
                  {i + 1}
                </Select.Item>
              ))}
              <Select.Item value={maxTicketsPerReservation.toString()}>
                {maxTicketsPerReservation}+
              </Select.Item>
            </Select.Content>
          </Select.Root>
          {claimFormReceived && (
            <Text size="1" color="gray">
              Ticket count cannot be changed after the claim form has been received
            </Text>
          )}
          {!claimFormReceived && hasLinkedClaimForm && hasTicketCountChanged && (
            <Text size="1" color="orange">
              This reservation has a linked claim form — updating the ticket count will require
              reconciling the claim form
            </Text>
          )}
        </Flex>
      </Flex>

      <Flex gap="3">
        <Flex direction="column" style={{ flex: 1 }}>
          <Text weight="bold">Phone Number</Text>
          <TextField.Root
            value={phone}
            onChange={(e) => onPhoneChange(formatPhoneNumber(e.target.value))}
          />
        </Flex>
        <Flex direction="column" style={{ flex: 1 }}>
          <Text weight="bold">Email</Text>
          <TextField.Root value={email} onChange={(e) => onEmailChange(e.target.value)} />
        </Flex>
      </Flex>

      <Flex direction="column">
        <Text weight="bold">Notes</Text>
        <TextArea
          placeholder="Add Notes"
          className="add-notes"
          maxLength={255}
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
        />
      </Flex>
    </>
  )
}
